import { useCallback } from 'react'
import { Picker } from '@/components/Picker'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { QcSignals } from '@/components/QcSignals'
import { Avatar, ProfileHover } from '@/components/Avatar'
import { WorkTree, type TreeTask } from '@/components/WorkTree'
import { PIPELINE_STAGES, REVIEW_STAGES, intakeStage, pipelineFilterKey, taskDisplayStage, workflowStage } from '@/lib/stages'
import { INTAKE_API, type IntakeSummary } from '@/lib/templateIntakes'
import { SCIENCE_PROGRAM, taskPrograms, programLabel } from '@/lib/programs'
import { useSmeDirectory } from '@/lib/smes'
import { useStickySearch } from '@/lib/stickySearch'
import type { QcSignals as QcSignalsValue, Task } from '@/lib/types'

const FILTERS = ['q', 'stage', 'program', 'sme_id', 'lifecycle']

export default function EngTasks() {
  const [params, setParams] = useStickySearch('eng-tasks', FILTERS)
  const rawStage = params.get('stage') ?? ''
  const smeId = params.get('sme_id') ?? ''
  const q = params.get('q') ?? ''
  const program = params.get('program') ?? ''
  const lifecycle = params.get('lifecycle') ?? 'active'
  // Physics filters by the six-stage board; other programs by the stored stage.
  const scientific = program.toLowerCase() === 'physics'
  const stage = scientific ? pipelineFilterKey(rawStage) : rawStage

  // Rows come from the website's own records; QC verdicts (partly the authoring service's) follow.
  const listed = useAsync(() => api.get<{ tasks: Task[] }>(`/eng/tasks?lifecycle=${lifecycle}&qc=later`), [lifecycle])
  const qc = useAsync(() => api.get<{ signals: Record<string, QcSignalsValue> }>('/eng/qc-signals'))
  // Approved and returned templates too, so every workflow sits in the template it came from.
  const intakes = useAsync(() => api.get<{ templates: IntakeSummary[] }>(INTAKE_API))
  const smes = useSmeDirectory()
  const signals = qc.data?.signals

  const set = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p) }
  const text = useCallback((value: string) => !q || value.toLowerCase().includes(q.toLowerCase()), [q])
  const matchTask = useCallback((t: TreeTask) => (!stage || (scientific ? workflowStage(t) : String(taskDisplayStage(t as Task))) === stage)
    && (!smeId || t.sme_id === smeId) && (!program || taskPrograms(t as Task).includes(program))
    && text(`${t.title} ${t.task_id} ${t.sme_email} ${programLabel(t as Task)}`), [stage, scientific, smeId, program, text])
  const matchTemplate = useCallback((i: IntakeSummary) => (!stage || intakeStage(i.status) === stage) && (!smeId || i.owner_id === smeId)
    && (!program || program === SCIENCE_PROGRAM) && text(`${i.title} ${i.template_id} ${i.owner_email}`), [stage, smeId, program, text])
  const taskHref = useCallback((t: TreeTask) => `/eng/tasks/${t.task_id}`, [])
  const templateHref = useCallback((i: IntakeSummary) => `/eng/templates/${i.template_id}`, [])
  const memberHref = useCallback((t: TreeTask, m: { task_id: string }) => `/eng/tasks/${t.task_id}?task=${m.task_id}&section=prompt`, [])

  return (
    <div>
      <div className="page-head">
        <div><h1>Tasks and workflows</h1></div>
      </div>
      <div className="filters">
        <input type="text" aria-label="Search tasks and workflows" placeholder="Search tasks and workflows…" value={q} onChange={(e) => set('q', e.target.value)} />
        <Picker label="Status" value={lifecycle} onChange={value => set('lifecycle', value)} options={[
          { value: 'active', label: 'Current' }, { value: 'retired', label: 'Retired' }, { value: 'all', label: 'All' },
        ]} />
        <Picker label="Stage" value={stage} onChange={value => set('stage', value)} options={[{ value: '', label: 'All stages' }, ...(scientific ? PIPELINE_STAGES.map(s => ({ value: s.key, label: `${s.num}. ${s.label}` })) : REVIEW_STAGES.map(s => ({ value: String(s.stage), label: `${s.num}. ${s.label}` })))]} />
        <Picker label="Program" value={program} onChange={value => set('program', value)} options={[{ value: '', label: 'All programs' }, ...[...new Set((listed.data?.tasks ?? []).flatMap(taskPrograms))].sort().map(value => ({ value, label: value }))]} />
        {smeId && <button className="sm" onClick={() => set('sme_id', '')}>Expert filter ✕</button>}
      </div>
      {listed.error && <div className="note err">{listed.error}</div>}
      {qc.error && !listed.error && <div className="note err">QC verdicts could not be loaded: {qc.error}</div>}
      <WorkTree tasks={listed.data?.tasks ?? []} templates={intakes.data?.templates ?? []} loading={listed.loading} empty="Nothing matches."
        matchTask={matchTask} matchTemplate={matchTemplate} taskHref={taskHref} templateHref={templateHref} memberHref={memberHref}
        decorate={(t, tag) => signals?.[t.task_id] ? <QcSignals signals={signals[t.task_id]}>{tag}</QcSignals> : tag}
        expert={({ task, intake }) => {
          const id = task?.sme_id ?? intake?.owner_id
          const user = id ? smes[id] : undefined
          const name = user?.name || task?.sme_email || intake?.owner_name || intake?.owner_email
          if (!name) return null
          return <span className="expert-cell">{user
            ? <ProfileHover user={user}><Avatar avatar={user.avatar} name={user.name || user.email} size={26} /></ProfileHover>
            : <Avatar name={name} size={26} />}<span>{name}</span></span>
        }} />
    </div>
  )
}
